import { Component, Input, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { FormControl } from '@angular/forms';
import { TreeViewItem } from '@core/components/fc-tree-view/fc-tree.types';
import { VideoInfoModalService } from '@core/components/video-info-modal/video-info-modal.service';
import { Maintenance } from '@core/interfaces';
import { TranslateService } from '@ngx-translate/core';
import { Subject } from 'rxjs';
import { debounceTime, takeUntil } from 'rxjs/operators';
import { TabLinksService } from '../../../dialog-create-maintenance/tabs/links/tab-links.service';

@Component({
  selector: 'tab-links',
  templateUrl: './tab-links.component.html',
  styleUrls: ['./tab-links.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class TabLinksComponent implements OnInit, OnDestroy {
  @Input()
  public maintenance: Maintenance;

  public hasLinkedItems = false;
  public searchControl = new FormControl();
  public locationLinks = [];
  public unlinkedEquipments = [];

  public destroy$ = new Subject<void>();

  constructor(
    private readonly tabLinksService: TabLinksService,
    private readonly translateService: TranslateService,
    private readonly videoInfoModalService: VideoInfoModalService,
  ) { }

  public ngOnInit(): void {
    this.populateLinkedItems();

    this.searchControl.valueChanges
      .pipe(takeUntil(this.destroy$), debounceTime(300))
      .subscribe(() => {
        this.populateLinkedItems();
      });

    this.tabLinksService.reloadItem$
      .pipe(takeUntil(this.destroy$))
      .subscribe((reloadItemSubject) => {
        const items = this.maintenance[reloadItemSubject.formControlName];
        const oldestItemToUpdateIndex = items.findIndex((item) => item.id === reloadItemSubject.updatedValue.id);
        items[oldestItemToUpdateIndex] = reloadItemSubject.updatedValue;
        this.populateLinkedItems();
      });
  }

  public ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  public populateLinkedItems(): void {
    const equipments = this.maintenance.equipments || [];
    const sectors = this.maintenance.locationSectors || [];
    const environments = this.maintenance.locationEnvironments || [];

    this.hasLinkedItems = Boolean(
      environments.length ||
      equipments.length ||
      sectors.length
    );

    if (!this.hasLinkedItems) {
      return;
    }

    const { sectors: sectorsLinks, unlinkedEquipments } = this.tabLinksService.linkSectorsEnvironmentsEquipments({
      sectors,
      environments,
      equipments
    });

    const locationLinksUnfiltered = this.tabLinksService.formatSectors({
      sectors: sectorsLinks,
      environments,
      equipments,
      maintenance: this.maintenance
    });

    const unlinkedEquipmentsUnfiltered = this.tabLinksService.formatUnlinkedEquipments({
      unlinkedEquipments,
      maintenance: this.maintenance
    });

    this.locationLinks = this.filter(locationLinksUnfiltered, this.searchControl.value);
    this.unlinkedEquipments = this.filter(unlinkedEquipmentsUnfiltered, this.searchControl.value);
  }

  private filter(items: TreeViewItem[], searchTerm: string): TreeViewItem[] {
    searchTerm = (searchTerm || '').toLowerCase().trim();

    return items.filter((item) => {
      if (item.children) {
        item.children = this.filter(item.children, searchTerm);
      }

      const text = (item.text || '').toLowerCase();
      const description = (item.description || '').toLowerCase();

      return text.includes(searchTerm) || description.includes(searchTerm) || (item.children && item.children.length) || this.parentMatch(item, searchTerm);
    });
  }

  private parentMatch(item: TreeViewItem, searchTerm: string): boolean {
    if (!item.parent) { return false; }

    searchTerm = (searchTerm || '').toLowerCase().trim();

    const text = (item.parent.text || '').toLowerCase();
    const description = (item.parent.description || '').toLowerCase();

    return text.includes(searchTerm) || description.includes(searchTerm) || this.parentMatch(item.parent, searchTerm);
  }

  public openDemonstrationVideoDialog(): void {
    this.videoInfoModalService.open({
      title: this.translateService.instant('MAINTENANCES.CREATE_DIALOG.TAB_LINKS.FEATURE.INTRO_VIDEO.TITLE'),
      description: this.translateService.instant('MAINTENANCES.CREATE_DIALOG.TAB_LINKS.FEATURE.INTRO_VIDEO.DESCRIPTION'),
      videoId: 'VQE_FyV8Gf0'
    });
  }
}
